import { useMemo } from 'react';
import { useGetSaLocationsQuery } from '@/graphql/generated/hooks';

/**
 * GraphQL-backed replacement for the former hardcoded `sa-locations` data.
 *
 * Fetches the whole Province -> District Municipality -> Local Municipality
 * hierarchy once (cached by Apollo) and exposes name-based lookup helpers
 * with the same signatures the address forms previously used, so callers
 * can keep storing names rather than ids.
 */
export function useSaLocations() {
  const { data, loading, error } = useGetSaLocationsQuery();

  return useMemo(() => {
    const provinces = data?.provinces ?? [];
    const notEmpty = (value: string | null | undefined): value is string =>
      !!value;

    const findProvince = (name: string) =>
      provinces.find(p => p.name?.toLowerCase() === name.toLowerCase());

    /** All province names. */
    const getProvinces = (): string[] =>
      provinces.map(p => p.name).filter(notEmpty);

    /** District/metropolitan municipality names within a province. */
    const getMunicipalities = (province: string): string[] => {
      if (!province) return [];
      const found = findProvince(province);
      return (found?.districtMunicipalities ?? [])
        .map(m => m.name)
        .filter(notEmpty);
    };

    /** Local municipality names within a province + district municipality. */
    const getLocalMunicipalities = (
      province: string,
      municipality: string,
    ): string[] => {
      if (!province || !municipality) return [];
      const found = findProvince(province)?.districtMunicipalities?.find(
        m => m.name?.toLowerCase() === municipality.toLowerCase(),
      );
      return (found?.localMunicipalities ?? [])
        .map(lm => lm.municipality)
        .filter(notEmpty);
    };

    return {
      getProvinces,
      getMunicipalities,
      getLocalMunicipalities,
      loading,
      error,
    };
  }, [data, loading, error]);
}
